import { inject, Injectable } from '@angular/core';
import { Auth0Client, createAuth0Client, RedirectLoginResult, User } from '@auth0/auth0-spa-js';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { LocalStorageUtils } from '../utils/localStorage.utils';
import { ConfigurationFacade } from '../facades/configuration.facade';
import { switchMap, take } from 'rxjs/operators';
import { EFeatureToggles } from '../configurations/common';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  AUTH0_COOKIE: string = `auth0.${environment.auth0Config.client_id}.is.authenticated`;
  requestedScopes: string = 'openid profile email';
  token: BehaviorSubject<string> = new BehaviorSubject<string>('');

  private auth0Client: Auth0Client = new Auth0Client({
    domain: environment.auth0Config.domain,
    clientId: environment.auth0Config.client_id,
    authorizationParams: {
      redirect_uri: `${globalThis.location.origin}`,
      audience: environment.auth0Config.audience,
      scope: this.requestedScopes,
      refresh_token: {
        rotation_type: 'rotating',
        expiration_type: 'expiring',
        token_lifetime: '2592000',
        leeway: 3,
      },
    },
    useRefreshTokensFallback: true,
    useRefreshTokens: true,
    cacheLocation: 'localstorage',
  });
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);

  /**
   * Convert promise output of handleRedirectCallback() to Observable. Must be a function which is returning
   * Observable<RedirectLoginResult>.
   *
   * @returns {Observable<RedirectLoginResult>}
   */
  handleRedirectCallback(): Observable<RedirectLoginResult> {
    return from(this.auth0Client.handleRedirectCallback());
  }

  getTokenSilently$: Observable<string> = from(this.getAuth0SilentToken());

  /**
   * Get silent token (acquired from Teamplay fleet or while logging in repeatedly). If "auth0Client" is not existing,
   * then create new one. If there is not existing any silent token, then returns empty string.
   *
   * @returns {Promise<string>}
   */
  async getAuth0SilentToken(): Promise<string> {
    let silentToken: string = '';
    try {
      if (!this.auth0Client) {
        this.auth0Client = await createAuth0Client({
          domain: environment.auth0Config.domain,
          clientId: environment.auth0Config.client_id,
          authorizationParams: {
            redirect_uri: `${globalThis.location.origin}`,
            audience: environment.auth0Config.audience,
            scope: this.requestedScopes,
          },
          useRefreshTokensFallback: true,
          useRefreshTokens: true,
          cacheLocation: 'localstorage',
        });
      }
      this.token.next(await this.auth0Client.getTokenSilently({
        timeoutInSeconds: 1,
      }));
      this.token.pipe().subscribe(async (token: string): Promise<void> => {
        if (token) {
          silentToken = token;
        }
      });
      return silentToken;
    } catch (e: unknown) {
      console.trace('Failed to get Auth0 silent token', e);
      return silentToken;
    }
  }

  /**
   * @returns {Observable<User>}
   */
  getUser$(): Observable<User | undefined> {
    return from(this.auth0Client.getUser());
  }

  /**
   * Login and redirect user to given "redirectPath."
   *
   * @param {string} redirectPath
   * @param {{}} queryParameters
   */
  login(redirectPath: string = '/', queryParameters: Record<string, string> = {}): void {
    LocalStorageUtils.clearKey(`lang`);
    LocalStorageUtils.clearKey(`previousPage`);

    from(this.auth0Client.loginWithRedirect({
      appState: {target: redirectPath, queryParams: queryParameters},
    }));
  }

  /**
   * Logout user - clear values from local storage.
   * If SLO is enabled, then cancel whole Healthineers ID session and redirect back to the application.
   * If SLO is disabled, then redirect only from application session.
   * @param {boolean} skipRedirect
   * @return {Observable<void>}
   */
  logout(skipRedirect: boolean): Observable<void> {
    if (skipRedirect) {
      LocalStorageUtils.clearKey(`lang`);
      LocalStorageUtils.clearKey(`previousPage`);
    }

    const clientId: string = environment.auth0Config.client_id;

    return this.configurationFacade.isFeatureEnabled(EFeatureToggles.SINGLE_LOGOUT).pipe(
      take(1),
      switchMap((isSingleLogout: boolean) => {
        if (isSingleLogout) {
          LocalStorageUtils.clearAuthTokens();
          globalThis.location.href = this.buildSloLogoutUrl(
            environment.auth0Config.mainTenantDomain,
            environment.auth0Config.domain,
            clientId,
            globalThis.location.origin,
          );
          // Return empty observable as this method needs to return Observable for old logout method (there is effect connected to old logic).
          return of();
        } else {
          return from(this.auth0Client.logout({
            clientId: environment.auth0Config.client_id,
            logoutParams: {
              returnTo: `${globalThis.location.origin}${skipRedirect ? '' : '?logoutRedirect=1'}`,
            },
          }));
        }
      })
    );
  }

  /**
   * Build the full SLO (Single Logout) redirect URL.
   * First logs out from the main tenant, which then redirects to the sub-tenant logout,
   * which finally redirects back to the application origin.
   *
   * @param {string} mainTenantDomain - e.g. 'login.siemens-healthineers.com'
   * @param {string} subTenantDomain - e.g. 'shs.login.siemens-healthineers.com'
   * @param {string} clientId
   * @param {string} appOrigin - e.g. 'https://shop.siemens-healthineers.com'
   * @returns {string} Full SLO redirect URL
   */
  buildSloLogoutUrl(mainTenantDomain: string, subTenantDomain: string, clientId: string, appOrigin: string): string {
    const subTenantLogoutUrl: string = `https://${subTenantDomain}/v2/logout?client_id=${clientId}&returnTo=${encodeURIComponent(appOrigin)}`;
    return `https://${mainTenantDomain}/v2/logout?returnTo=${encodeURIComponent(subTenantLogoutUrl)}`;
  }

  getAuthenticatedCookie(): boolean {
    return document.cookie.split(';').some((item) => item.trim().startsWith(`${this.AUTH0_COOKIE}=`) && item.includes(`${this.AUTH0_COOKIE}=true`));
  }
}
